import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Leaf, Battery, Building2, Download, FileSpreadsheet, Info, Zap, Wind } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { calculateROI, API } from "@/lib/api";
import { IMG_INVESTOR_MEETING } from "@/lib/images";

const INVESTOR_IMAGE = "/generated/e3_premium_hub.png";

const OWNERSHIP_TABLE = [
    { stations: 1, investment: "₹25 L", ebitda: "₹12 L", value: "₹96 L" },
    { stations: 5, investment: "₹1.25 Cr", ebitda: "₹60 L", value: "₹4.8 Cr" },
    { stations: 10, investment: "₹2.5 Cr", ebitda: "₹1.2 Cr", value: "₹9.6 Cr" },
    { stations: 25, investment: "₹6.25 Cr", ebitda: "₹3 Cr", value: "₹24 Cr" },
    { stations: 50, investment: "₹12.5 Cr", ebitda: "₹6 Cr", value: "₹48 Cr" },
    { stations: 100, investment: "₹25 Cr", ebitda: "₹12 Cr", value: "₹96 Cr" },
];

const SCENARIO_TABLE = [
    { s: "Conservative", rev: "₹147 Cr", ebitda: "₹36 Cr", val: "₹360 Cr" },
    { s: "Upside", rev: "₹230 Cr", ebitda: "₹58 Cr", val: "₹580 Cr" },
    { s: "Aggressive", rev: "₹328 Cr", ebitda: "₹82 Cr", val: "₹820 Cr" },
];

// Simple animated counter
const Counter = ({ end, suffix = "", prefix = "", duration = 1400 }) => {
    const [val, setVal] = useState(0);
    useEffect(() => {
        let start = 0;
        const startTime = performance.now();
        const step = (now) => {
            const t = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(start + (end - start) * eased);
            if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [end, duration]);
    const formatted = end % 1 === 0 ? Math.round(val).toLocaleString() : val.toFixed(2);
    return <span>{prefix}{formatted}{suffix}</span>;
};

export const Investor = () => {
    const [inputs, setInputs] = useState({
        stations: 10,
        sessions_per_day: 12,
        tariff: 18,
        energy_cost: 9,
        capex_per_station_lakh: 25,
        opex_per_station_lakh: 3,
        energy_per_session_kwh: 45,
        valuation_multiple: 8,
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const runCalc = async (payload) => {
        setLoading(true);
        try {
            const data = await calculateROI(payload);
            setResult(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        runCalc(inputs);
        // eslint-disable-next-line
    }, [inputs]);

    const update = (key, value) => setInputs((s) => ({ ...s, [key]: value }));

    return (
        <div data-testid="investor-page">
            <section className="pt-24 pb-16 md:pt-32 md:pb-20 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6">For Investors</div>
                    <h1 data-testid="investor-hero-title" className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-stone-900 max-w-5xl mb-10">
                        Invest once. Build an <span className="italic e3-gradient-text">infrastructure asset.</span>
                    </h1>
                    <p className="text-stone-600 text-lg md:text-xl leading-relaxed max-w-3xl">
                        Participate in India's electric mobility transition for the next decade.
                        These illustrative scenarios show what a portfolio of E3 stations could look like.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={`${API}/investor/report.xlsx`}
                            data-testid="investor-download-excel-hero"
                            className="e3-btn-primary"
                        >
                            <FileSpreadsheet className="w-4 h-4" /> Download Full Excel Model
                        </a>
                        <a href="#roi-calculator" className="e3-btn-ghost">
                            Open ROI Calculator <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                    <p className="text-xs text-stone-500 mt-4">6-sheet workbook · Assumptions · Single station · Portfolio · Network · Carbon · Disclaimer</p>
                </div>
            </section>

            {/* Market Stats */}
            <section className="py-20 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6">India's EV Infrastructure Opportunity</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="e3-card-static">
                            <div className="font-serif text-5xl md:text-6xl font-light e3-gradient-text mb-3">
                                <Counter end={1.32} suffix="M" />
                            </div>
                            <div className="text-stone-700 font-medium mb-2">Public charging stations required</div>
                            <div className="text-sm text-stone-500">India's estimated need by 2030 · <em>IBEF</em></div>
                        </div>
                        <div className="e3-card-static">
                            <div className="font-serif text-5xl md:text-6xl font-light e3-gradient-text mb-3">
                                <Counter end={31} suffix="%" />
                            </div>
                            <div className="text-stone-700 font-medium mb-2">CAGR through end of decade</div>
                            <div className="text-sm text-stone-500">Indian EV charging market · <em>Mordor Intelligence</em></div>
                        </div>
                        <div className="e3-card-static">
                            <div className="font-serif text-5xl md:text-6xl font-light e3-gradient-text mb-3">
                                1 : <Counter end={235} />
                            </div>
                            <div className="text-stone-700 font-medium mb-2">Chargers to EVs ratio today</div>
                            <div className="text-sm text-stone-500">Public network significantly undersupplied · <em>TOI</em></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* KWH & REVENUE DEEP-DIVE */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">kWh & Revenue — Deep Dive</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                How a single station <span className="italic e3-gradient-text">makes money.</span>
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                Every rupee of revenue on an E3 station starts as a kWh dispensed to an EV.
                                Here's the full waterfall — session by session, unit by unit.
                            </p>
                        </div>
                    </div>

                    {/* Waterfall */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12" data-testid="investor-waterfall">
                        <WaterfallCard step="01" label="Sessions per day" value="12" hint="Base case · moderate utilisation" testid="wf-sessions" />
                        <WaterfallCard step="02" label="Energy per session" value="45 kWh" hint="Indian EV avg (60–75 kWh battery)" testid="wf-per-session" />
                        <WaterfallCard step="03" label="Daily units" value="540 kWh" hint="12 × 45 kWh" testid="wf-daily" />
                        <WaterfallCard step="04" label="Annual units" value="197,100 kWh" hint="540 × 365 days" testid="wf-annual" highlight />
                    </div>

                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm mb-8">
                        <Table data-testid="investor-station-econ-table">
                            <TableHeader>
                                <TableRow className="bg-stone-100 hover:bg-stone-100">
                                    <TableHead className="text-stone-700 font-semibold py-5">Line item</TableHead>
                                    <TableHead className="text-stone-700 font-semibold text-right">Rate</TableHead>
                                    <TableHead className="text-stone-700 font-semibold text-right">Annual value (₹)</TableHead>
                                    <TableHead className="text-stone-700 font-semibold">Formula</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow className="bg-emerald-50/60">
                                    <TableCell className="font-semibold text-emerald-800 py-4">Gross charging revenue</TableCell>
                                    <TableCell className="text-right text-stone-700">₹ 18 / kWh</TableCell>
                                    <TableCell className="text-right font-semibold e3-gradient-text">35.48 L</TableCell>
                                    <TableCell className="text-xs text-stone-500">197,100 × 18 ÷ 100,000</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="text-stone-800 py-4">— Energy cost (DISCOM)</TableCell>
                                    <TableCell className="text-right text-stone-700">₹ 9 / kWh</TableCell>
                                    <TableCell className="text-right text-stone-700">(17.74 L)</TableCell>
                                    <TableCell className="text-xs text-stone-500">197,100 × 9 ÷ 100,000</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="text-stone-800 py-4">— Site OPEX</TableCell>
                                    <TableCell className="text-right text-stone-700">flat</TableCell>
                                    <TableCell className="text-right text-stone-700">(3.00 L)</TableCell>
                                    <TableCell className="text-xs text-stone-500">rent + O&M + insurance + connectivity</TableCell>
                                </TableRow>
                                <TableRow className="bg-emerald-50 border-t-2 border-emerald-300">
                                    <TableCell className="font-semibold text-emerald-900 py-4">Annual EBITDA</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell className="text-right font-bold e3-gradient-text text-lg">14.74 L</TableCell>
                                    <TableCell className="text-xs text-stone-600">= gross revenue − energy − OPEX</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="text-stone-800 py-4">CAPEX</TableCell>
                                    <TableCell className="text-right text-stone-700">one-time</TableCell>
                                    <TableCell className="text-right text-stone-700">25.00 L</TableCell>
                                    <TableCell className="text-xs text-stone-500">charger + civil + electrical + commissioning</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="text-stone-800 py-4">Cash yield on CAPEX</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell className="text-right text-stone-900 font-semibold">59%</TableCell>
                                    <TableCell className="text-xs text-stone-500">EBITDA ÷ CAPEX × 100</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className="text-stone-800 py-4">Simple payback</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell className="text-right text-stone-900 font-semibold">1.70 yrs</TableCell>
                                    <TableCell className="text-xs text-stone-500">CAPEX ÷ annual EBITDA</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>

                    {/* Sensitivity by sessions/day */}
                    <div className="e3-overline mb-4">Sensitivity · How utilisation drives revenue</div>
                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm mb-6">
                        <Table data-testid="investor-sensitivity-table">
                            <TableHeader>
                                <TableRow className="bg-stone-100 hover:bg-stone-100">
                                    <TableHead className="font-semibold text-stone-700 py-5">Sessions / day</TableHead>
                                    <TableHead className="font-semibold text-stone-700 text-right">Daily kWh</TableHead>
                                    <TableHead className="font-semibold text-stone-700 text-right">Annual kWh</TableHead>
                                    <TableHead className="font-semibold text-stone-700 text-right">Gross Revenue (₹)</TableHead>
                                    <TableHead className="font-semibold text-stone-700 text-right">EBITDA (₹)</TableHead>
                                    <TableHead className="font-semibold text-stone-700 text-right">Payback (yrs)</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {[
                                    { s: 6, d: 270, a: 98550, r: 17.74, e: 5.87, p: 4.26, tag: "Low" },
                                    { s: 8, d: 360, a: 131400, r: 23.65, e: 8.83, p: 2.83, tag: "Conservative" },
                                    { s: 12, d: 540, a: 197100, r: 35.48, e: 14.74, p: 1.70, tag: "Base", featured: true },
                                    { s: 16, d: 720, a: 262800, r: 47.30, e: 20.66, p: 1.21, tag: "Upside" },
                                    { s: 20, d: 900, a: 328500, r: 59.13, e: 26.57, p: 0.94, tag: "Aggressive" },
                                ].map((r, i) => (
                                    <TableRow key={i} className={r.featured ? "bg-emerald-50/60 border-emerald-200" : "border-stone-200"} data-testid={`investor-sens-row-${i}`}>
                                        <TableCell className="py-4 font-medium text-stone-900">
                                            {r.s} <span className="ml-2 text-xs text-stone-500">· {r.tag}</span>
                                        </TableCell>
                                        <TableCell className="text-right text-stone-700 tabular-nums">{r.d}</TableCell>
                                        <TableCell className="text-right text-stone-700 tabular-nums">{r.a.toLocaleString()}</TableCell>
                                        <TableCell className="text-right text-stone-800 tabular-nums">{r.r.toFixed(2)} L</TableCell>
                                        <TableCell className={`text-right font-semibold tabular-nums ${r.featured ? "e3-gradient-text" : "text-stone-900"}`}>{r.e.toFixed(2)} L</TableCell>
                                        <TableCell className="text-right text-stone-700 tabular-nums">{r.p.toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <details className="text-sm text-stone-500 leading-relaxed max-w-4xl" data-testid="investor-station-econ-methodology">
                        <summary className="cursor-pointer font-semibold text-stone-700 mb-2 flex items-center gap-2 hover:text-stone-900">
                            <Info className="w-3.5 h-3.5" /> Methodology & assumptions (click to expand)
                        </summary>
                        <div className="mt-3 space-y-2 text-xs">
                            <p><strong>Session size:</strong> 45 kWh per session is the weighted average across Indian EVs launched in the last 24 months (Tata Nexon LR, MG ZS EV, Mahindra BE 6, Hyundai Creta EV, BYD Atto 3 — typically charging from ~20% to ~80% state of charge).</p>
                            <p><strong>Tariff (₹18/kWh):</strong> Blended B2C + fleet-contract price. B2C rack rates in India today range ₹18–24 per kWh; fleet contracts typically ₹14–17 per kWh. Model uses a conservative blended midpoint.</p>
                            <p><strong>Energy cost (₹9/kWh):</strong> Commercial DISCOM tariff for HT/LT industrial connections in most Indian states, including demand charges and fixed load. State-specific EV tariffs (₹6.5–8) can reduce this further.</p>
                            <p><strong>OPEX (₹3 L/year):</strong> Site rent (₹0.5–1 L), preventive maintenance (₹0.8 L), insurance (₹0.4 L), 4G connectivity + cloud (₹0.3 L), local staff/security allocation (₹0.5 L).</p>
                            <p><strong>CAPEX (₹25 L):</strong> Cubenz 120 kW dual-gun DC charger (₹18 L), transformer + LT panel + cabling (₹3 L), civil + canopy + signage (₹2.5 L), commissioning & training (₹1.5 L).</p>
                            <p><strong>Illustrative only.</strong> Actual results vary with site, tariffs, utilisation, DISCOM policy and market conditions. Not a guarantee or forecast.</p>
                        </div>
                    </details>
                </div>
            </section>

            {/* Year 1 target infographic */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #6abf4b 0px, transparent 40%), radial-gradient(circle at 80% 70%, #2b8fb0 0px, transparent 40%)" }} />
                <div className="max-w-7xl mx-auto relative">
                    <div className="e3-overline mb-6 text-stone-400">E3 Charge Network · Year 1</div>
                    <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] mb-16 max-w-4xl">
                        500 stations. ₹125 Cr network value. <span className="italic e3-gradient-text">The beginning.</span>
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 stagger">
                        {[
                            { n: "500", l: "Stations" },
                            { n: "1,000", l: "Chargers" },
                            { n: "82M", l: "kWh Annual" },
                            { n: "₹147 Cr", l: "Annual Revenue (Base)" },
                        ].map((s) => (
                            <div key={s.l} className="border-t border-stone-800 pt-6">
                                <div className="font-serif text-5xl md:text-6xl font-light e3-gradient-text mb-2">{s.n}</div>
                                <div className="text-stone-400 text-sm">{s.l}</div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-20 rounded-2xl overflow-hidden border border-stone-800 bg-stone-900/50">
                        <Table data-testid="investor-scenario-table">
                            <TableHeader>
                                <TableRow className="border-stone-800 hover:bg-stone-900">
                                    <TableHead className="text-stone-400 text-xs tracking-widest uppercase">Scenario</TableHead>
                                    <TableHead className="text-stone-400 text-xs tracking-widest uppercase">Annual Revenue</TableHead>
                                    <TableHead className="text-stone-400 text-xs tracking-widest uppercase">EBITDA (25%)</TableHead>
                                    <TableHead className="text-stone-400 text-xs tracking-widest uppercase">Illustrative Valuation (10x)</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {SCENARIO_TABLE.map((r) => (
                                    <TableRow key={r.s} className="border-stone-800 hover:bg-stone-800/40">
                                        <TableCell className="text-white py-4 font-medium">{r.s}</TableCell>
                                        <TableCell className="text-stone-300">{r.rev}</TableCell>
                                        <TableCell className="text-stone-300">{r.ebitda}</TableCell>
                                        <TableCell className="e3-gradient-text font-semibold">{r.val}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </section>

            {/* INTERACTIVE ROI CALCULATOR */}
            <section id="roi-calculator" className="py-24 md:py-32 px-6 md:px-10 scroll-mt-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
                        <div className="md:col-span-7">
                            <div className="e3-overline mb-6">Interactive · ROI Calculator</div>
                            <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900">
                                What could your portfolio look like?
                            </h2>
                        </div>
                        <div className="md:col-span-5">
                            <p className="text-stone-600 text-lg leading-relaxed">
                                Adjust the sliders. See live projections. Illustrative only —
                                actual results depend on utilisation, tariffs and market conditions.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Inputs */}
                        <div className="lg:col-span-5 e3-card-static space-y-8">
                            <SliderRow
                                label="Number of Stations"
                                testid="roi-slider-stations"
                                value={inputs.stations}
                                min={1} max={100} step={1}
                                display={inputs.stations}
                                onChange={(v) => update("stations", v)}
                            />
                            <SliderRow
                                label="Charging Sessions per Day"
                                testid="roi-slider-sessions"
                                value={inputs.sessions_per_day}
                                min={4} max={25} step={1}
                                display={inputs.sessions_per_day}
                                onChange={(v) => update("sessions_per_day", v)}
                            />
                            <SliderRow
                                label="Charging Tariff (₹ / unit)"
                                testid="roi-slider-tariff"
                                value={inputs.tariff}
                                min={12} max={26} step={0.5}
                                display={`₹${inputs.tariff}`}
                                onChange={(v) => update("tariff", v)}
                            />
                            <SliderRow
                                label="Energy Cost (₹ / unit)"
                                testid="roi-slider-energy-cost"
                                value={inputs.energy_cost}
                                min={5} max={14} step={0.5}
                                display={`₹${inputs.energy_cost}`}
                                onChange={(v) => update("energy_cost", v)}
                            />
                            <SliderRow
                                label="CAPEX per Station (₹ Lakhs)"
                                testid="roi-slider-capex"
                                value={inputs.capex_per_station_lakh}
                                min={18} max={45} step={1}
                                display={`₹${inputs.capex_per_station_lakh} L`}
                                onChange={(v) => update("capex_per_station_lakh", v)}
                            />
                            <SliderRow
                                label="Valuation Multiple (x EBITDA)"
                                testid="roi-slider-multiple"
                                value={inputs.valuation_multiple}
                                min={5} max={20} step={0.5}
                                display={`${inputs.valuation_multiple}x`}
                                onChange={(v) => update("valuation_multiple", v)}
                            />
                        </div>

                        {/* Outputs */}
                        <div className="lg:col-span-7 space-y-6">
                            {result && (
                                <>
                                    <div className="e3-gradient-bg rounded-2xl p-8 md:p-10 text-white relative overflow-hidden">
                                        <div className="absolute -top-32 -right-32 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
                                        <div className="relative">
                                            <div className="text-white/70 text-xs uppercase tracking-[0.2em] mb-3">Illustrative Portfolio Value</div>
                                            <div className="font-serif text-6xl md:text-7xl font-light" data-testid="roi-output-valuation">
                                                ₹{result.illustrative_valuation_cr.toFixed(2)} Cr
                                            </div>
                                            <div className="mt-4 text-white/80">
                                                on ₹{(result.total_investment_lakh / 100).toFixed(2)} Cr investment · <span className="font-semibold">{result.wealth_multiple.toFixed(1)}x</span> wealth multiple
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <OutputCard
                                            label="Total Investment"
                                            value={`₹${result.total_investment_lakh.toFixed(1)} L`}
                                            hint={`${result.stations} stations × CAPEX`}
                                            testid="roi-output-investment"
                                        />
                                        <OutputCard
                                            label="Annual Revenue"
                                            value={`₹${result.annual_revenue_lakh.toFixed(1)} L`}
                                            hint={`${(result.annual_units_kwh / 1000).toFixed(0)}K kWh sold`}
                                            testid="roi-output-revenue"
                                        />
                                        <OutputCard
                                            label="Annual EBITDA"
                                            value={`₹${result.annual_ebitda_lakh.toFixed(1)} L`}
                                            hint={`Cash yield: ${result.cash_yield_pct.toFixed(1)}%`}
                                            highlight
                                            testid="roi-output-ebitda"
                                        />
                                        <OutputCard
                                            label="Payback"
                                            value={`${result.payback_years.toFixed(1)} yrs`}
                                            hint={`CO₂ avoided: ${result.co2_avoided_tonnes.toFixed(0)} T/yr`}
                                            testid="roi-output-payback"
                                        />
                                    </div>
                                </>
                            )}
                            {loading && !result && (
                                <div className="text-stone-500 text-sm">Calculating…</div>
                            )}
                        </div>
                    </div>

                    <p className="mt-10 text-xs text-stone-500 max-w-3xl leading-relaxed">
                        <strong>Disclaimer:</strong> All figures, assumptions and projections are illustrative
                        and intended solely to demonstrate potential opportunities. Actual results may vary
                        depending on utilisation, tariffs, regulations, market conditions and other factors.
                        These projections do not constitute a guarantee, commitment or assurance of future performance.
                    </p>
                </div>
            </section>

            {/* WEALTH TABLE */}
            <section className="py-24 md:py-32 px-6 md:px-10 bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="e3-overline mb-6">Founder's Circle · Wealth Table</div>
                    <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] text-stone-900 mb-6 max-w-4xl">
                        A single station is a start. A portfolio is a platform.
                    </h2>
                    <p className="text-stone-600 text-lg mb-12 max-w-2xl">
                        Illustrative wealth creation potential across station counts.
                    </p>

                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-white">
                        <Table data-testid="investor-wealth-table">
                            <TableHeader>
                                <TableRow className="bg-stone-100 hover:bg-stone-100">
                                    <TableHead className="text-stone-700 font-semibold py-5">Stations Owned</TableHead>
                                    <TableHead className="text-stone-700 font-semibold">Total Investment</TableHead>
                                    <TableHead className="text-stone-700 font-semibold">Annual EBITDA</TableHead>
                                    <TableHead className="text-stone-700 font-semibold">Illustrative Value</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {OWNERSHIP_TABLE.map((r, i) => (
                                    <TableRow key={r.stations} className="border-stone-200 hover:bg-stone-50">
                                        <TableCell className="font-serif text-2xl text-stone-900 py-5">{r.stations}</TableCell>
                                        <TableCell className="text-stone-700">{r.investment}</TableCell>
                                        <TableCell className="text-stone-700">{r.ebitda}</TableCell>
                                        <TableCell className="e3-gradient-text font-bold text-lg">{r.value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </section>

            {/* IMPACT */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center mb-14">
                    <div className="md:col-span-6">
                        <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                            <img src={INVESTOR_IMAGE} alt="Investor discussion" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="e3-overline mb-6">Impact</div>
                        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-stone-900 mb-8">
                            More than a charger. A <span className="italic e3-gradient-text">decarbonisation platform.</span>
                        </h2>
                        <div className="grid grid-cols-2 gap-6">
                            <ImpactStat n="492M" l="Green kilometres enabled annually (500 stations)" />
                            <ImpactStat n="26,700 T" l="CO₂ avoided per year (500 stations)" />
                            <ImpactStat n="98.5M kWh" l="Clean energy dispensed annually" />
                            <ImpactStat n="< 0.1%" l="Of India's 2030 charger requirement" />
                        </div>
                    </div>
                </div>

                {/* Green km & CO₂ methodology */}
                <div className="max-w-7xl mx-auto" data-testid="investor-carbon-methodology">
                    <div className="e3-overline mb-4">Methodology · How we calculate</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="e3-card-static">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl e3-gradient-soft flex items-center justify-center">
                                    <Zap className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-2xl text-stone-900">Green kilometres</h3>
                            </div>
                            <p className="text-stone-700 text-sm leading-relaxed mb-4">
                                Every kWh dispensed by an E3 charger replaces petrol or diesel kilometres. We convert kWh to km using the average efficiency of Indian EVs.
                            </p>
                            <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 font-mono text-xs text-stone-800 space-y-1 mb-3">
                                <div>Green km = Annual kWh × km/kWh</div>
                                <div className="text-stone-500">= 197,100 kWh × 6.0 km/kWh</div>
                                <div className="text-emerald-700 font-semibold">= 1,182,600 km per station / year</div>
                            </div>
                            <p className="text-xs text-stone-500 leading-relaxed">
                                6.0 km/kWh is the weighted average across Indian EVs — Tata Nexon EV, MG ZS EV, Mahindra BE 6, Hyundai Creta EV, BYD Atto 3, based on manufacturer-published real-world figures.
                            </p>
                        </div>

                        <div className="e3-card-static">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl e3-gradient-soft flex items-center justify-center">
                                    <Wind className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-2xl text-stone-900">CO₂ avoided</h3>
                            </div>
                            <p className="text-stone-700 text-sm leading-relaxed mb-4">
                                Net CO₂ avoided is the difference between petrol vehicle emissions and the emissions of the grid electricity used to charge the EV.
                            </p>
                            <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 font-mono text-xs text-stone-800 space-y-1 mb-3">
                                <div>Petrol emission = 143 g CO₂/km</div>
                                <div>Grid emission = 713 g CO₂/kWh ÷ 6 km/kWh</div>
                                <div className="text-stone-500">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; = 118.8 g CO₂/km</div>
                                <div>Net avoided = 143 − 118.8 = 24.2 g/km</div>
                                <div>× 1,182,600 km × 1 station × 1 year</div>
                                <div className="text-emerald-700 font-semibold">= 28.6 tonnes CO₂ / station / year</div>
                            </div>
                            <p className="text-xs text-stone-500 leading-relaxed">
                                Grid factor from Central Electricity Authority (CEA) India 2024. Petrol emission from ARAI test cycle averaged across popular Indian passenger cars. As India's grid decarbonises (target ~500 g/kWh by 2030), avoided CO₂ per station will grow to ~40 tonnes/year.
                            </p>
                        </div>
                    </div>

                    <details className="mt-6 text-sm text-stone-500 leading-relaxed max-w-4xl">
                        <summary className="cursor-pointer font-semibold text-stone-700 mb-2 flex items-center gap-2 hover:text-stone-900">
                            <Info className="w-3.5 h-3.5" /> Full assumptions & sources (click to expand)
                        </summary>
                        <div className="mt-3 space-y-2 text-xs">
                            <p><strong>km per kWh (6.0):</strong> Weighted average — Tata Nexon EV LR (5.5), MG ZS EV (6.2), Mahindra BE 6 (5.8), Hyundai Creta EV (6.4), BYD Atto 3 (6.3). Real-world Indian conditions.</p>
                            <p><strong>Grid emission factor (713 g CO₂/kWh):</strong> India's average grid emission factor for FY 2023-24, published by CEA (Central Electricity Authority) baseline database. This includes coal, gas, hydro, nuclear and renewables.</p>
                            <p><strong>Petrol emission (143 g CO₂/km):</strong> Weighted average of top-selling Indian passenger cars (Maruti Baleno, Hyundai i20, Tata Nexon petrol, Kia Sonet) using ARAI-certified fuel economy figures (~16.5 kmpl average) and standard petrol emission factor (2.31 kg CO₂/litre).</p>
                            <p><strong>Network figures (500 stations):</strong> Assumes Base scenario (12 sessions/day). Green km = 1,182,600 × 500 ÷ 1M = 591M. CO₂ avoided = 28.6 T × 500 ≈ 14,300 T. Higher utilisation scenarios (16-20 sessions/day) push these numbers 30-70% higher.</p>
                            <p><strong>Grand rounded figures above:</strong> Reflect network deployment ramp — not all 500 stations operate at Year 1 base utilisation from day one; ramp-up factor applied.</p>
                            <p><strong>Not accounted for:</strong> Well-to-wheel upstream emissions of petrol (~15% additional). Tyre/brake particulate emissions. Land-use benefits. These would increase E3's positive impact further.</p>
                        </div>
                    </details>
                </div>
            </section>

            {/* CTA BAND — Excel download */}
            <section className="px-6 md:px-10 mb-16">
                <div className="max-w-7xl mx-auto rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="investor-excel-cta-band">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl e3-gradient-bg flex items-center justify-center shrink-0 text-white">
                            <FileSpreadsheet className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="font-serif text-2xl md:text-3xl font-medium text-stone-900 mb-1">Take the full model with you.</div>
                            <div className="text-stone-600 text-sm">6-sheet Excel workbook · assumptions, single-station, portfolio, network, carbon impact, disclaimer.</div>
                        </div>
                    </div>
                    <a href={`${API}/investor/report.xlsx`} data-testid="investor-download-excel-band" className="e3-btn-primary shrink-0">
                        <Download className="w-4 h-4" /> Download .xlsx
                    </a>
                </div>
            </section>

            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-6xl mx-auto e3-gradient-bg rounded-3xl p-12 md:p-20 text-white relative overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    <div className="relative">
                        <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tighter leading-[1.05] mb-6 max-w-3xl">
                            Ready to become an infrastructure owner?
                        </h2>
                        <p className="text-white/85 text-lg md:text-xl max-w-2xl mb-10">
                            Speak with the E3 team. Explore Founding Partner benefits and territory availability.
                        </p>
                        <Link to="/contact" data-testid="investor-final-cta" className="bg-white text-stone-900 px-8 py-3.5 rounded-full font-semibold hover:bg-stone-100 inline-flex items-center gap-2">
                            Talk to E3 Investment Team <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

const SliderRow = ({ label, value, min, max, step, display, onChange, testid }) => (
    <div>
        <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-stone-700">{label}</label>
            <span className="font-serif text-xl e3-gradient-text tabular-nums" data-testid={`${testid}-value`}>{display}</span>
        </div>
        <Slider
            data-testid={testid}
            value={[value]}
            min={min}
            max={max}
            step={step}
            onValueChange={(v) => onChange(v[0])}
            className="w-full"
        />
    </div>
);

const OutputCard = ({ label, value, hint, highlight, testid }) => (
    <div className={`rounded-2xl p-6 border ${highlight ? "bg-emerald-50 border-emerald-200" : "bg-white border-stone-200"}`} data-testid={testid}>
        <div className="e3-overline mb-3">{label}</div>
        <div className="font-serif text-3xl md:text-4xl font-light text-stone-900 mb-2">{value}</div>
        <div className="text-xs text-stone-500">{hint}</div>
    </div>
);

const ImpactStat = ({ n, l }) => (
    <div>
        <div className="font-serif text-4xl md:text-5xl font-light e3-gradient-text mb-2">{n}</div>
        <div className="text-sm text-stone-600 leading-snug">{l}</div>
    </div>
);

const WaterfallCard = ({ step, label, value, hint, highlight, testid }) => (
    <div className={`lg:col-span-3 rounded-2xl p-6 border transition-all ${highlight ? "e3-gradient-bg text-white border-transparent shadow-xl" : "bg-white border-stone-200"}`} data-testid={testid}>
        <div className={`font-mono text-xs mb-3 ${highlight ? "text-white/70" : "text-stone-400"}`}>{step}</div>
        <div className={`text-xs font-semibold uppercase tracking-widest mb-2 ${highlight ? "text-white/80" : "text-stone-500"}`}>{label}</div>
        <div className={`font-serif text-3xl md:text-4xl font-light mb-2 tabular-nums ${highlight ? "text-white" : "text-stone-900"}`}>{value}</div>
        <div className={`text-xs ${highlight ? "text-white/70" : "text-stone-500"}`}>{hint}</div>
    </div>
);

